import { Injectable } from '@angular/core'
import { createEffect, Actions, ofType } from '@ngrx/effects'
import { switchMap, catchError, map, withLatestFrom, tap, exhaustMap, mergeMap, filter } from 'rxjs'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { EventTicketPurchasesApiService } from './event-ticket-purchases-api.service'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import { SelectedProfileFacade } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.facade'
import { GbStripeService } from 'libs/gb-front/payment/data-access-stripe/src/lib/gb-stripe.service'
import { GbPaymentService } from 'libs/gb-front/payment/util/src/lib/gb-payment.service'
import { GbPaymentStepperViewController } from 'libs/gb-front/payment/util/src/lib/gb-payment-stepper/gb-payment-stepper-view-controller.service'
import { GbFrontDialogService } from 'libs/gb-front/shared/util/src/lib/gb-front-dialog-service/gb-front-dialog.service'
import { GbErrorMessageService } from 'libs/shared/utils/helpers/src/lib/gb-error-message/gb-error-message.service'
import { GbMessageDialogService } from 'libs/gb-front/shared/util/src/lib/gb-message-dialog/gb-message-dialog.service'
import { GbPaymentDialogComponent } from 'libs/gb-front/payment/util/src/lib/gb-payment-dialog/gb-payment-dialog.component'
import { GbPaymentDialogConfig, PaymentTypeEnum } from 'libs/gb-front/payment/util/src/lib/gb-payment.models'
import { Action } from '@ngrx/store'
import {
  EventTicketPackageEntity,
  EventTicketPackagesFacade,
} from 'libs/gb-front/event-tickets/data-access-event-ticket-packages/src'
import * as AuthActions from 'libs/gb-front/auth/data-access/src/lib/+state/auth.actions'
import * as EventTicketPurchasesActions from './event-ticket-purchases.actions'
import * as PaymentActions from 'libs/gb-front/payment/data-access/src/lib/+state/payment.actions'
import * as SelectedProfileActions from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.actions'
import { EventTicketPurchasePayload } from './event-ticket-purchases.models'
import { HttpErrorResponse } from '@angular/common/http'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src/lib/+state/loader.models'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { EventTicketPurchasesFacade } from './event-ticket-purchases.facade'
import { TranslateService } from '@ngx-translate/core'
import { VerificationMethodEnum } from 'libs/gb-front/shared/models/shared.models'
import { GbEventTicketMultiAssignComponent } from 'libs/shared/event-tickets/feature/src/lib/gb-event-ticket-multi-assign/gb-event-ticket-multi-assign.component'

@Injectable()
export class EventTicketPurchasesEffects {
  startEventTicketPurchase$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventTicketPurchasesActions.startEventTicketPurchase),
      withLatestFrom(this.authFacade.authProfile$, this.selectedProfileFacade.selectedProfile$),
      switchMap(([action, authProfile, selectedProfile]) => {
        const actions: Action[] = [EventTicketPurchasesActions.updateAmount({ amount: action.amount })]

        if (!authProfile || authProfile?.uuid === '') {
          actions.push(
            AuthActions.openRegisterLoginDialog({
              checkProfile: true,
              // omit "Verify" button in email to maintain ticket purchase flow on register/verify
              registerExtras: { verification_method: VerificationMethodEnum.Code },
              onSuccess: () => EventTicketPurchasesActions.openPaymentDialog(),
            }),
          )
        } else if (selectedProfile) {
          actions.push(
            SelectedProfileActions.syncProfile({
              showLoader: true,
              showSnackBarOnFail: true,
              checkProfile: true,
              onSuccess: () => EventTicketPurchasesActions.openPaymentDialog(),
            }),
          )
        }

        return actions
      }),
    )
  })

  openPaymentDialog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventTicketPurchasesActions.openPaymentDialog),
      withLatestFrom(
        this.eventTicketPackagesFacade.selectedEventTicketPackage$,
        this.eventTicketPurchasesFacade.currentAmount$,
      ),
      filter(([_action, eventTicketPackage]) => !!eventTicketPackage),
      exhaustMap(([_, eventTicketPackage, amount]) => {
        const pAmount = amount * eventTicketPackage!.price

        const paymentDialogConfig: GbPaymentDialogConfig = {
          item: eventTicketPackage,
          paymentType: PaymentTypeEnum.EventTicket,
          paymentAmount: pAmount,
          quantity: amount,
          dynamicStep: {
            label: 'SHARED.EVENT_TICKETS.MULTI_ASSIGN.STEP_LABEL',
            title: 'SHARED.EVENT_TICKETS.MULTI_ASSIGN.HEADER',
            component: GbEventTicketMultiAssignComponent,
          },
        }

        return this.gbFrontDialogService.getDialogObs(GbPaymentDialogComponent, {
          data: paymentDialogConfig,
          disableClose: true,
        })
      }),
      switchMap((result) => {
        const action = result
          ? // on success
            EventTicketPurchasesActions.clearPayment()
          : // on cancel
            PaymentActions.cancelPayment()

        return [action]
      }),
    )
  })

  clearPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.clearPayment),
      switchMap(() => [
        PaymentActions.clearSelectedPaymentMethod(),
        EventTicketPurchasesActions.clearAmount(),
        EventTicketPurchasesActions.clearSelectedEventTicketPurchase(),
      ]),
    ),
  )

  placeEventTicketPurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.placeEventTicketPurchase),
      withLatestFrom(
        this.authFacade.authentication$,
        this.selectedProfileFacade.selectedProfileId$,
        this.eventTicketPackagesFacade.selectedEventTicketPackage$,
        this.auctionFacade.auctionUrlName$,
        this.eventTicketPurchasesFacade.currentAmount$,
        this.auctionFacade.selectedPaymentMethod$,
        this.gbPaymentService.tippingFeeAmount$,
        this.gbPaymentService.applyPaymentMethodFee$,
        this.gbPaymentService.donationAmount$,
      ),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.PlaceEventTicketPurchase)),
      mergeMap(
        ([
          action,
          authentication,
          selectedProfileId,
          eventTicketPackage,
          urlName,
          amount,
          selectedPaymentMethod,
          tippingFeeAmount,
          applyPaymentMethodFee,
          donationAmount,
        ]) => {
          const payload: EventTicketPurchasePayload = {
            profile: selectedProfileId,
            ticket_package: { uuid: eventTicketPackage?.uuid },
            quantity: amount,
            delegate: authentication.profile.profile,
            donation_amount: donationAmount,
          }

          if (tippingFeeAmount) {
            payload.tip_amount = tippingFeeAmount
          }

          if (this.ticketRequiresPayment(eventTicketPackage)) {
            payload.payment_method = selectedPaymentMethod?.uuid
            payload.apply_fee = applyPaymentMethodFee
          }

          return this.eventTicketPurchasesApiService.createEventTicketPurchase(urlName, payload).pipe(
            map((response) =>
              EventTicketPurchasesActions.placeEventTicketPurchaseSuccess({ eventTicketPurchase: response }),
            ),
            catchError(async (error: HttpErrorResponse) =>
              EventTicketPurchasesActions.placeEventTicketPurchaseFailure({ error }),
            ),
          )
        },
      ),
    ),
  )

  handlePaymentRequired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPurchasesActions.placeEventTicketPurchaseSuccess),
        filter((action) => !!action.eventTicketPurchase.payment_pending),
        map((action) => {
          this.gbPaymentStepperViewController.progressStepper() // progress to 'payment' step
          this.gbStripeService.payment = action.eventTicketPurchase.payment
        }),
      ),
    { dispatch: false },
  )

  handlePaymentNotRequired$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.placeEventTicketPurchaseSuccess),
      filter((action) => !action.eventTicketPurchase.payment_pending),
      // any non-stripe payment method does not require any extra step
      map(() => EventTicketPurchasesActions.finaliseEventTicketPurchase()),
    ),
  )

  placeEventTicketPurchaseFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPurchasesActions.placeEventTicketPurchaseFailure),
        tap((action) => {
          const errorMessage =
            this.gbErrorMessageService.getErrorMessage(action.error.error) ||
            'EVENT_TICKET_PURCHASES.ERROR_MESSAGE.BODY'
          this.gbMessageDialogService.showDialog({
            title: 'EVENT_TICKET_PURCHASES.ERROR_MESSAGE.TITLE',
            body: errorMessage,
            messageType: 'warning',
          })
        }),
      ),
    { dispatch: false },
  )

  hideEventTicketPackagePurchaseLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EventTicketPurchasesActions.placeEventTicketPurchaseSuccess,
          EventTicketPurchasesActions.placeEventTicketPurchaseFailure,
        ),
        map(() => this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.PlaceEventTicketPurchase)),
      ),
    { dispatch: false },
  )

  finaliseEventTicketPurchase$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPurchasesActions.finaliseEventTicketPurchase),
        tap(
          () => this.gbPaymentStepperViewController.progressStepper(), // progress to 'assign' tickets step
        ),
      ),
    { dispatch: false },
  )

  showSuccessDialogOnFinaliseEventTicketPurchase$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPurchasesActions.finaliseEventTicketPurchaseSuccess),
        withLatestFrom(
          this.selectedProfileFacade.selectedProfile$,
          this.eventTicketPackagesFacade.selectedEventTicketPackage$,
        ),
        map(([_, selectedProfile, ticketPackage]) => {
          const email = selectedProfile?.email || selectedProfile?.invite_email
          const messageKey = email
            ? 'EVENT_TICKET.SUCCESS_MESSAGE_DESCRIPTION'
            : 'EVENT_TICKET.SUCCESS_MESSAGE_DESCRIPTION_NO_EMAIL'
          const params = email ? { email } : { ticketPackage: ticketPackage?.name }

          this.gbMessageDialogService.showDialog({
            title: 'EVENT_TICKET.SUCCESS_MESSAGE_HEADER',
            body: this.translateService.instant(messageKey, params),
            messageType: 'success',
            eventTicket: true,
          })
        }),
      ),
    { dispatch: false },
  )

  closePaymentDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EventTicketPurchasesActions.finaliseEventTicketPurchaseSuccess,
          EventTicketPurchasesActions.placeEventTicketPurchaseFailure,
        ),
        map((action) => this.gbFrontDialogService.closeDialog(GbPaymentDialogComponent, !('error' in action))),
      ),
    { dispatch: false },
  )

  syncEventTicketPurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPurchasesActions.syncEventTicketPurchase),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      switchMap(([action, urlName]) =>
        this.eventTicketPurchasesApiService.syncEventTicketPurchase(urlName, action.uuid).pipe(
          map((response) =>
            EventTicketPurchasesActions.syncEventTicketPurchaseSuccess({
              update: {
                id: response.uuid,
                changes: response,
              },
            }),
          ),
          catchError(async (error: HttpErrorResponse) =>
            EventTicketPurchasesActions.syncEventTicketPurchaseFailure({ error }),
          ),
        ),
      ),
    ),
  )

  constructor(
    private readonly actions$: Actions,
    private eventTicketPurchasesApiService: EventTicketPurchasesApiService,
    private eventTicketPurchasesFacade: EventTicketPurchasesFacade,
    private eventTicketPackagesFacade: EventTicketPackagesFacade,
    private auctionFacade: AuctionFacade,
    private authFacade: AuthFacade,
    private selectedProfileFacade: SelectedProfileFacade,
    private gbStripeService: GbStripeService,
    private gbPaymentService: GbPaymentService,
    private gbPaymentStepperViewController: GbPaymentStepperViewController,
    private gbFrontDialogService: GbFrontDialogService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbMessageDialogService: GbMessageDialogService,
    private loaderService: GbLoaderService,
    private translateService: TranslateService,
  ) {}

  ticketRequiresPayment(eventTicketPackage: EventTicketPackageEntity | undefined): boolean {
    return !!eventTicketPackage && eventTicketPackage.price > 0
  }
}
